import React, { useMemo } from 'react';
import { Icon, ProgressBar } from '@openedx/paragon';
import { RiStarFill, RiStarLine } from '@remixicon/react';
import { FormattedMessage, useIntl } from '@edx/frontend-platform/i18n';

import messages from './messages';

const SIZE_PX = { sm: 14, md: 18, lg: 22 };

const StarRating = ({ rating, max = 5, size = 'sm' }) => {
  const full = Math.max(0, Math.min(max, Math.round(rating || 0)));
  const pixels = SIZE_PX[size];
  return (
    <span
      className={`reviews-stars reviews-stars--${size}`}
      style={{ '--reviews-star-size': `${pixels}px` }}
    >
      {Array.from({ length: max }, (_, i) => {
        const filled = i < full;
        return (
          <Icon
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            src={filled ? RiStarFill : RiStarLine}
            className={`reviews-stars__star ${filled ? 'reviews-stars__star--filled' : 'reviews-stars__star--empty'}`}
          />
        );
      })}
    </span>
  );
};

const RatingSnapshot = ({ distribution }) => {
  const intl = useIntl();
  const { counts, total, avg } = distribution;

  const pctByStar = useMemo(() => {
    const m = {};
    [5, 4, 3, 2, 1].forEach((n) => {
      const c = counts[n] || 0;
      m[n] = total > 0 ? (c / total) * 100 : 0;
    });
    return m;
  }, [counts, total]);

  return (
    <section className="rating-snapshot">
      <h3 className="rating-snapshot__title">
        {intl.formatMessage(messages.courseRatingHeading)}
      </h3>
      <div className="rating-snapshot__score-row">
        <div className="rating-snapshot__score">
          {Number(avg || 0).toFixed(1)}
        </div>
        <div className="rating-snapshot__out-of">
          {intl.formatMessage(messages.outOfFive)}
        </div>
      </div>
      <div className="rating-snapshot__meta">
        <StarRating rating={avg} size="sm" />
        <span className="rating-snapshot__count">
          {intl.formatMessage(messages.totalRatings, { count: total.toLocaleString() })}
        </span>
      </div>
      <div className="rating-snapshot__distribution">
        {[5, 4, 3, 2, 1].map((star) => (
          <div key={star} className="rating-snapshot__row">
            <span className="rating-snapshot__row-label">
              {intl.formatMessage(messages.starRowLabel, { count: star })}
            </span>
            <ProgressBar
              now={pctByStar[star]}
              variant="primary"
              className="rating-snapshot__row-bar"
            />
            <span className="rating-snapshot__row-percent">
              {pctByStar[star].toFixed(0)}%
            </span>
          </div>
        ))}
      </div>
    </section>
  );
};

const formatDate = (iso, intl) => {
  if (!iso) return '';
  try {
    return intl.formatDate(new Date(iso), { year: 'numeric', month: 'short', day: 'numeric' });
  } catch (e) {
    return '';
  }
};

const ReviewCard = ({ review }) => {
  const intl = useIntl();
  const name = review.user?._AuthUser?.username
    || intl.formatMessage(messages.learnerFallbackName, { id: review.reviewerId });
  const initial = name.charAt(0).toUpperCase();
  const rating = review.rating || 0;
  const title = review.reviewTitle || '';
  const description = review.reviewDescription || '';
  return (
    <article className="review-card">
      <header className="review-card__header">
        <div className="review-card__identity">
          <div className="review-card__avatar review-card__avatar--placeholder">
            {initial}
          </div>
          <div className="review-card__identity-meta">
            <div className="review-card__name">{name}</div>
            <StarRating rating={rating} />
          </div>
        </div>
        <span className="review-card__date">{formatDate(review.createdAt, intl)}</span>
      </header>
      {title && <h4 className="review-card__title">{title}</h4>}
      {description && <p className="review-card__description">{description}</p>}
    </article>
  );
};

const CourseReviews = ({ reviews, distribution }) => {
  if (!reviews || reviews.length === 0) return null;

  return (
    <section className="course-detail__reviews">
      <h2 className="course-detail__section-title">
        <FormattedMessage {...messages.courseReviewsHeading} />
      </h2>
      <div className="course-reviews__layout">
        <RatingSnapshot distribution={distribution} />
        <div className="review-list__items">
          {reviews.map((review) => (
            <ReviewCard key={review.id} review={review} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default CourseReviews;
